import { useCallback, useEffect, useState } from "react";
import {
  Loader2, ShieldCheck, Star, Bike, Users, MessageSquare, Eye,
  ChevronDown, ChevronUp, Check, X, Pause, Trash2, EyeOff, Phone, Mail, Zap,
} from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { useAuth, authFetch } from "@/lib/auth";
import { api, INR } from "@/lib/marketplace";

const STATUS_STYLE = {
  active: "text-[#00cccc] border-[#00cccc]/40 bg-[#00cccc]/10",
  paused: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  rejected: "text-red-400 border-red-400/30 bg-red-400/10",
  sold: "text-zinc-300 border-white/20 bg-white/[0.06]",
  deleted: "text-red-500 border-red-500/30 bg-red-500/10",
};
const LEAD_STATUS_STYLE = {
  new: "text-[#00cccc] border-[#00cccc]/40 bg-[#00cccc]/10",
  contacted: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  closed: "text-zinc-300 border-white/20 bg-white/[0.06]",
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export default function AdminDashboard() {
  const { user, loading, login } = useAuth();
  const [tab, setTab] = useState("listings");
  const [overview, setOverview] = useState(null);
  const [listings, setListings] = useState(null);
  const [leads, setLeads] = useState(null);
  const [optState, setOptState] = useState(null); // null | "busy" | {optimized, skipped, failed}

  const optimizePhotos = async () => {
    setOptState("busy");
    try {
      const r = await authFetch("/api/admin/photos/optimize", { method: "POST" });
      setOptState(r.ok ? await r.json() : null);
    } catch { setOptState(null); }
  };

  const loadOverview = useCallback(async () => {
    const r = await authFetch("/api/admin/overview");
    if (r.ok) setOverview(await r.json());
  }, []);
  const loadListings = useCallback(async () => {
    const r = await authFetch("/api/admin/listings");
    if (r.ok) setListings((await r.json()).items || []);
  }, []);
  const loadLeads = useCallback(async () => {
    const r = await authFetch("/api/admin/leads");
    if (r.ok) setLeads((await r.json()).items || []);
  }, []);

  useEffect(() => { document.title = "Admin — Biker Boulevard"; }, []);
  useEffect(() => {
    if (!user?.is_admin) return;
    loadOverview(); loadListings(); loadLeads();
  }, [user, loadOverview, loadListings, loadLeads]);

  const patchListing = async (id, body) => {
    const r = await authFetch(`/api/admin/listings/${id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    });
    if (r.ok) {
      const updated = await r.json();
      setListings((ls) => ls.map((l) => (l.listing_id === id ? { ...l, ...updated } : l)));
      loadOverview();
    }
  };
  const patchLead = async (id, status) => {
    const r = await authFetch(`/api/admin/leads/${id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status }),
    });
    if (r.ok) {
      const updated = await r.json();
      setLeads((ls) => ls.map((l) => (l.lead_id === id ? { ...l, ...updated } : l)));
      loadOverview();
    }
  };
  const deleteListing = async (id) => {
    if (!window.confirm("Permanently delete this listing? It will be removed from the site AND the database — photos and chats included. This CANNOT be recovered.")) return;
    const r = await authFetch(`/api/admin/listings/${id}`, { method: "DELETE" });
    if (r.ok) {
      setListings((ls) => ls.filter((l) => l.listing_id !== id));
      loadOverview();
    }
  };

  let body;
  if (loading) {
    body = <div className="pt-40 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#00cccc] mx-auto" /></div>;
  } else if (!user) {
    body = (
      <div className="pt-32 max-w-md mx-auto text-center px-5">
        <h2 className="text-2xl font-bold">Admin access requires sign in</h2>
        <button onClick={login} data-testid="admin-login" className="mt-6 px-6 py-3 rounded-full bg-[#00cccc] text-black font-semibold">Continue with Google</button>
      </div>
    );
  } else if (!user.is_admin) {
    body = (
      <div className="pt-32 max-w-md mx-auto text-center px-5" data-testid="admin-denied">
        <ShieldCheck className="w-10 h-10 text-red-400 mx-auto" />
        <h2 className="mt-4 text-2xl font-bold">Access denied</h2>
        <p className="mt-2 text-sm text-zinc-400">This area is restricted to Biker Boulevard admins.</p>
      </div>
    );
  } else {
    body = (
      <div className="pt-24 pb-20 max-w-[1200px] mx-auto px-5 md:px-10" data-testid="admin-page">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#00cccc]">// Admin Control Panel</span>
            <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">Command Center</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={optimizePhotos} disabled={optState === "busy"} data-testid="admin-optimize-photos"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00cccc]/40 text-[#00cccc] text-xs font-semibold hover:bg-[#00cccc]/10 disabled:opacity-50">
              {optState === "busy" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} Optimize Photos
            </button>
            {optState && optState !== "busy" && (
              <span className="text-[11px] text-zinc-400" data-testid="optimize-result">{optState.optimized} optimized · {optState.skipped} already fast{optState.failed ? ` · ${optState.failed} failed` : ""}</span>
            )}
          </div>
        </div>

        {/* Overview */}
        {overview && (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="admin-overview">
            {[
              [Bike, "Active", overview.listings?.active || 0],
              [Pause, "Paused", overview.listings?.paused || 0],
              [ShieldCheck, "Verified", overview.verified || 0],
              [Star, "Featured", overview.featured || 0],
              [Users, "New Leads", overview.leads?.new || 0],
              [MessageSquare, "Enquiries", overview.enquiries || 0],
            ].map(([Icon, k, v]) => (
              <div key={k} className="p-3 rounded-xl bg-white/[0.04] border border-white/10">
                <div className="flex items-center gap-1.5 text-[11px] font-sans font-semibold uppercase tracking-wider text-zinc-300"><Icon className="w-3.5 h-3.5 text-[#00cccc]" />{k}</div>
                <div className="mt-1 text-xl font-bold text-white">{v}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="mt-8 flex gap-2 border-b border-white/10 pb-px">
          <TabBtn active={tab === "listings"} onClick={() => setTab("listings")} testid="admin-tab-listings">
            Marketplace Listings {listings ? `(${listings.length})` : ""}
          </TabBtn>
          <TabBtn active={tab === "leads"} onClick={() => setTab("leads")} testid="admin-tab-leads">
            Direct Sell Leads {leads ? `(${leads.length})` : ""}
          </TabBtn>
        </div>

        <div className="mt-6">
          {tab === "listings" && (
            !listings ? <Spinner /> : listings.length === 0 ? <Empty text="No listings yet." /> : (
              <div className="space-y-3" data-testid="admin-listings-list">
                {listings.map((l) => <ListingRow key={l.listing_id} item={l} onPatch={patchListing} onDelete={deleteListing} />)}
              </div>
            )
          )}
          {tab === "leads" && (
            !leads ? <Spinner /> : leads.length === 0 ? <Empty text="No direct sell leads yet." /> : (
              <div className="space-y-3" data-testid="admin-leads-list">
                {leads.map((l) => <LeadRow key={l.lead_id} lead={l} onStatus={patchLead} />)}
              </div>
            )
          )}
        </div>
      </div>
    );
  }

  return (
    <main className="relative bg-[#0D0F12] text-white min-h-screen">
      <Navbar />
      <div className="fixed inset-0 pointer-events-none -z-10"><div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(0,204,204,0.06), transparent 50%), #0D0F12" }} /></div>
      {body}
      <Footer />
    </main>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-5 py-2.5 rounded-t-xl text-sm font-semibold border-b-2 transition-all ${active ? "text-[#00cccc] border-[#00cccc] bg-[#00cccc]/5" : "text-zinc-400 border-transparent hover:text-white"}`}>
      {children}
    </button>
  );
}
const Spinner = () => <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#00cccc] mx-auto" /></div>;
const Empty = ({ text }) => <p className="py-16 text-center text-sm text-zinc-500">{text}</p>;

function ListingRow({ item, onPatch, onDelete }) {
  const [open, setOpen] = useState(false);
  const cover = item.photo_ids?.[item.primary_photo_index] || item.photo_ids?.[0];
  const st = item.status || "active";
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid={`admin-listing-${item.listing_id}`}>
      <div className="p-3 sm:p-4 flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-16 h-12 rounded-lg overflow-hidden bg-[#1A1E23] shrink-0">
            {cover && <img src={api.photoUrl(cover)} alt="" className="w-full h-full object-cover" />}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white truncate">{item.registration_year} · {item.brand} {item.model} {item.variant || ""}</div>
            <div className="text-xs text-zinc-400 truncate">{item.seller_name} · {item.seller_city}, {item.seller_state} · {INR(item.expected_price)}</div>
            <div className="mt-1 flex items-center gap-2 text-[10px] text-zinc-500 font-mono">
              <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{item.views || 0}</span>
              <span>{fmtDate(item.created_at)}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`px-2.5 py-1 rounded-full text-[10px] font-mono uppercase border ${STATUS_STYLE[st] || STATUS_STYLE.active}`} data-testid={`listing-status-${item.listing_id}`}>{st}</span>
          <ToggleBtn on={!!item.verified} onClick={() => onPatch(item.listing_id, { verified: !item.verified })} testid={`toggle-verified-${item.listing_id}`} icon={ShieldCheck} label="Verified" />
          <ToggleBtn on={!!item.featured} onClick={() => onPatch(item.listing_id, { featured: !item.featured })} testid={`toggle-featured-${item.listing_id}`} icon={Star} label="Featured" />
          {st !== "active" && (
            <ActionBtn onClick={() => onPatch(item.listing_id, { status: "active" })} testid={`approve-${item.listing_id}`} icon={Check} label="Approve" tone="cyan" />
          )}
          {st === "active" && (
            <>
              <ActionBtn onClick={() => onPatch(item.listing_id, { status: "paused" })} testid={`pause-${item.listing_id}`} icon={Pause} label="Pause" />
              <ActionBtn onClick={() => onPatch(item.listing_id, { status: "rejected" })} testid={`reject-${item.listing_id}`} icon={X} label="Reject" tone="red" />
            </>
          )}
          {st !== "deleted" && (
            <ActionBtn onClick={() => onPatch(item.listing_id, { status: "deleted" })} testid={`remove-${item.listing_id}`} icon={EyeOff} label="Remove" tone="red" />
          )}
          <button onClick={() => onDelete(item.listing_id)} data-testid={`delete-forever-${item.listing_id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-red-500/40 text-red-400 hover:bg-red-500/15 hover:border-red-500 transition-all">
            <Trash2 className="w-3.5 h-3.5" /> Delete Forever
          </button>
          <button onClick={() => setOpen((v) => !v)} data-testid={`enquiries-toggle-${item.listing_id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-xs text-zinc-300 hover:border-[#00cccc]/40 hover:text-[#00cccc] transition-all">
            <MessageSquare className="w-3.5 h-3.5" /> {item.enquiry_count || 0} {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>
      </div>
      {open && <Enquiries listingId={item.listing_id} />}
    </div>
  );
}

function ToggleBtn({ on, onClick, testid, icon: Icon, label }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${on ? "bg-[#00cccc]/15 border-[#00cccc] text-[#00cccc]" : "border-white/10 text-zinc-400 hover:border-white/20 hover:text-white"}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}
function ActionBtn({ onClick, testid, icon: Icon, label, tone }) {
  const tones = { cyan: "hover:border-[#00cccc]/50 hover:text-[#00cccc]", red: "hover:border-red-400/50 hover:text-red-400" };
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border border-white/10 text-zinc-300 transition-all ${tones[tone] || tones.cyan}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

function Enquiries({ listingId }) {
  const [items, setItems] = useState(null);
  const [thread, setThread] = useState(null); // { convo_id, messages }
  useEffect(() => {
    (async () => {
      const r = await authFetch(`/api/admin/conversations?listing_id=${listingId}`);
      if (r.ok) setItems((await r.json()).items || []);
      else setItems([]);
    })();
  }, [listingId]);
  const openThread = async (cid) => {
    const r = await authFetch(`/api/admin/conversations/${cid}/messages`);
    if (r.ok) setThread({ convo_id: cid, messages: (await r.json()).messages || [] });
  };
  return (
    <div className="border-t border-white/10 bg-white/[0.04] p-4" data-testid={`enquiries-${listingId}`}>
      {!items ? <Spinner /> : items.length === 0 ? <p className="text-xs text-zinc-500">No buyer enquiries on this listing yet.</p> : (
        <div className="space-y-2">
          {items.map((c) => (
            <div key={c.conversation_id} className="rounded-lg border border-white/10 bg-white/[0.03]">
              <button onClick={() => (thread?.convo_id === c.conversation_id ? setThread(null) : openThread(c.conversation_id))}
                className="w-full p-3 flex items-center gap-3 text-left" data-testid={`thread-toggle-${c.conversation_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-white">{c.buyer_name} <span className="text-zinc-500 font-normal">→ {c.seller_name}</span></div>
                  <div className="text-[11px] text-zinc-400 truncate">{c.last_message || "—"}</div>
                </div>
                <span className="text-[10px] font-mono text-zinc-500 shrink-0">{c.message_count} msgs · {fmtDate(c.last_message_at)}</span>
              </button>
              {thread?.convo_id === c.conversation_id && (
                <div className="px-3 pb-3 space-y-1.5 max-h-56 overflow-y-auto" data-testid={`thread-${c.conversation_id}`}>
                  {thread.messages.map((m) => (
                    <div key={m.message_id} className="text-[12px] px-3 py-1.5 rounded-lg bg-white/[0.04] text-zinc-300">
                      <span className="text-[10px] font-mono text-[#00cccc] mr-2">{m.sender_id === c.buyer_id ? c.buyer_name : c.seller_name}</span>
                      {m.text}
                      <span className="text-[9px] text-zinc-600 ml-2">{fmtDate(m.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LeadRow({ lead, onStatus }) {
  const st = lead.status || "new";
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex flex-col lg:flex-row lg:items-center gap-3" data-testid={`admin-lead-${lead.lead_id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-white">{lead.full_name}</div>
        <div className="text-xs text-zinc-400">{lead.brand} {lead.model} {lead.variant || ""} · {lead.registration_year} · {Number(lead.kms_driven || 0).toLocaleString("en-IN")} km</div>
        <div className="mt-1 text-xs text-zinc-400">Expected: <span className="text-[#00cccc] font-semibold">{INR(parseInt(lead.expected_price) || 0)}</span> · {lead.city}, {lead.state}</div>
        <div className="mt-1.5 flex flex-wrap items-center gap-3 text-[11px] text-zinc-500">
          <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</span>
          <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{lead.email}</span>
          <span className="font-mono">{fmtDate(lead.submitted_at)}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`px-2.5 py-1 rounded-full text-[10px] font-mono uppercase border ${LEAD_STATUS_STYLE[st] || LEAD_STATUS_STYLE.new}`} data-testid={`lead-status-${lead.lead_id}`}>{st}</span>
        <select value={st} onChange={(e) => onStatus(lead.lead_id, e.target.value)} data-testid={`lead-status-select-${lead.lead_id}`}
          className="px-3 py-1.5 bg-[#15181C] border border-white/10 rounded-lg text-xs text-white" style={{ colorScheme: "dark" }}>
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="closed">Closed</option>
        </select>
      </div>
    </div>
  );
}
